/******************************************** STYLING *********************************************/
const s = require('./autocomplete.scss');

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import AlgoliaSearch from 'algoliasearch';

import {observer} from 'mobx-react';
import {action, observable, toJS} from 'mobx';
import {get, flatten} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {CIAutocomplete} from './autocomplete-component';

/**************************************** TYPE DEFINITIONS ****************************************/
export interface ReactAlgoliaSearchProps {
    indexName: string;
    filters?: string;
    hitsPerPage?: number;
    defaultValue?: string;
    currentLanguage?: string;
    displayKey?: string | ((item: any) => string);
    onSelected?: (value: string, item: any) => void;
    inputProps?: React.HTMLAttributes<HTMLInputElement>;
    wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
    /**
     * Object to configure basic (simple === match) client-side filtering
     *
     * resultObjPath: the path to dig into on the returned object
     * allowedMatches: array of items to allow for the value at the property at
     *                 resultObjPath
     *
     * Example:
     *     allowedResults value:
     *       {resultObjPath: 'name', allowedMatches: ['jo', 'jen', 'jack']}
     *     Data:
     *       [{name: 'jo', id: 1}, {name: 'jen', id: 2}, {name: 'smit', id: 3}]
     *     Values allowed in the output:
     *       [{name: 'jo', id: 1}, {name: 'jen', id: 2}]
     */
    allowedResults?: {resultObjPath: string; allowedMatches: any[]};
}

/********************************************* CONFIG *********************************************/
const algoliaClient = AlgoliaSearch(process.env.ALGOLIA_APPID, process.env.ALGOLIA_APIKEY);
const defaults = {hits: 7, key: `name`, filters: ``};

/********************************************* EXPORT *********************************************/
@observer
export class ReactAlgoliaSearch extends React.Component<ReactAlgoliaSearchProps> {
    algoliaIndex: AlgoliaSearch.Index;

    /**
     * Stored query sent to Algolia (to search through data set for)
     */
    @observable
    query = ``;

    /**
     * Stored result of current query sent to Algolia
     */
    @observable
    results = [];

    /**
     * Set/store value of the current stored query sent to Algolia
     */
    @action
    private updateQuery = (query: string) => (this.query = query);

    /**
     * Set/store the result of the last query sent to Algolia
     */
    @action
    private updateResults = (results: any[]) => (this.results = results);

    /**
     * Update query to match default value when it changes also empties results
     * to avoid issues
     */
    componentDidUpdate(prevProps) {
        if (this.props.defaultValue !== prevProps.defaultValue) {
            this.updateQuery(this.props.defaultValue);
            this.updateResults([]);
        }
    }

    /**
     * Initializes algolia index on component mount and sets query to default
     * value or empty string
     */
    componentDidMount() {
        this.algoliaIndex = algoliaClient.initIndex(this.props.indexName);
        this.updateQuery(this.props.defaultValue || ``);
    }

    /**
     * Get display value of list item taking language into account (defaults to
     * "name" prop)
     */
    public getItemValue = (item: any) => {
        const {currentLanguage} = this.props;
        const key = this.props.displayKey || defaults.key;

        if (typeof key === `function`) return key(item);
        else return currentLanguage ? item[currentLanguage][key] : item[key];
    };

    /**
     * Updates query value on input change event as well as fetching updated
     * search results
     */
    public handleChange = (_event: React.FormEvent<HTMLInputElement>, value: string) => {
        this.updateQuery(value);
        this.getSearchResults();
    };

    /**
     * Resets query value on input blur event to prevent entering values
     * outside of dataset
     */
    public handleBlur = () => {
        if (this.query) this.updateQuery(this.props.defaultValue || ``);
        else this.props.onSelected(null, null);
    };

    /**
     * Updates query & calls selected handler on dropdown menu item select event
     */
    public handleSelect = (value: string, item: any) => {
        this.updateQuery(value);
        this.props.onSelected(value, item);
    };

    /**
     * Fetch results from algolia index using supplied search options
     */
    private getSearchResults() {
        const searchOptions = {
            query: this.query,
            filters: this.props.filters || defaults.filters,
            hitsPerPage: this.props.hitsPerPage || defaults.hits,
        };
        this.algoliaIndex.search(searchOptions, (err, data) => {
            if (err) this.updateResults([]);
            else this.updateResults(data.hits);
        });
    }

    render() {

        // Filter such that only items at [props.allowedResults.resultObjPath]
        // in [this.results] that match an item in
        // [props.allowedResults.allowedMatches] are displayed
        let allowedResults = this.results;
        if (this.results && this.results.length > 0 && this.props.allowedResults) {
            allowedResults = this.results.filter(result => {
                const valAtResultObjPath = get(result, this.props.allowedResults.resultObjPath);
                return (
                    valAtResultObjPath &&
                    this.props.allowedResults.allowedMatches.find(
                        allowedMatch => valAtResultObjPath === allowedMatch
                    )
                );
            });
        }

        return (
            <CIAutocomplete
                value={this.query}
                selectOnBlur={true}
                autoHighlight={true}
                items={this.props.allowedResults ? toJS(allowedResults) : toJS(this.results)}
                onChange={this.handleChange}
                onSelect={this.handleSelect}
                getItemValue={this.getItemValue}
                wrapperProps={this.props.wrapperProps}
                inputProps={{
                    ...this.props.inputProps,
                    onBlur: this.handleBlur,
                    className: cn(
                        get(this, `props.inputProps.className`),
                        s[`city-dropdown-placeholder`]
                    ),
                }}
            />
        );
    }
}

ReactAlgoliaSearch.displayName = `ReactAlgoliaSearch`;
